<template>
	<div>
		<error-list :errors="page.errors"></error-list>
		<b-toast id="changes-saved" variant="success" solid title="Changes saved" toaster="b-toaster-top-center">
			Your changes have been saved.
		</b-toast>

		<b-alert :show="showNoExist" variant="info" dismissible>
			A design phase has not yet been entered for this project. Complete the form to create one.
		</b-alert>

		<b-form @submit.prevent="save">
			<b-row>
				<b-col md>
					<b-form-group label="Project" :invalid-feedback="requiredFeedback($v.query.project)">
						<b-typeahead v-model.trim="$v.query.project.$model" required placeholder="Type to search projects by number, name, or dam..."
									 :data="options.projects == null ? [] : options.projects"
									 :serializer="s => s.name" @hit="item.projectId = $event.id;"
									 :max-matches="100" :minMatchingChars="1">
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Assigned A&amp;E" :invalid-feedback="requiredFeedback($v.item.assignedAe)">
						<b-typeahead v-model.trim="$v.item.assignedAe.$model"
								:data="setOptions.assignedAe"
								:minMatchingChars="1" showAllResults showOnFocus>
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Point of Contact" :invalid-feedback="requiredFeedback($v.item.pointOfContact)">
						<b-typeahead v-model.trim="$v.item.pointOfContact.$model"
								:data="setOptions.pointOfContact"
								:minMatchingChars="1" showAllResults showOnFocus>
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Status" :invalid-feedback="requiredFeedback($v.item.status)">
						<b-input-group append="%">
							<b-form-input type="number" step="any" v-model.trim="$v.item.status.$model" :state="getValidState($v.item.status)"></b-form-input>
						</b-input-group>
					</b-form-group>

					<b-form-group label="Approving Agent" :invalid-feedback="requiredFeedback($v.item.approvingAgent)">
						<b-typeahead v-model.trim="$v.item.approvingAgent.$model"
									 :data="setOptions.approvingAgent"
									 :minMatchingChars="1" showAllResults showOnFocus>
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Final Status" :invalid-feedback="requiredFeedback($v.item.finalStatus)">
						<b-typeahead v-model.trim="$v.item.finalStatus.$model"
									 :data="setOptions.finalStatuses"
									 :minMatchingChars="1" showAllResults showOnFocus>
						</b-typeahead>
					</b-form-group>

					<b-form-group label="Final Date" :invalid-feedback="requiredFeedback($v.item.finalDate)">
						<b-form-input type="date" v-model.trim="$v.item.finalDate.$model" :state="getValidState($v.item.finalDate)"></b-form-input>
					</b-form-group>

					<b-form-group label="Final Construction Documents (CD) Date Received" :invalid-feedback="requiredFeedback($v.item.finalContractDate)">
						<b-form-input type="date" v-model.trim="$v.item.finalContractDate.$model" :state="getValidState($v.item.finalContractDate)"></b-form-input>
					</b-form-group>
				</b-col>
			</b-row>

			<b-form-group label="Notes" :invalid-feedback="requiredFeedback($v.item.notes)" class="mb-4">
				<b-form-textarea v-model.trim="$v.item.notes.$model" :state="getValidState($v.item.notes)" rows="3"></b-form-textarea>
			</b-form-group>

			<fixed-action-bar>
				<save-button :saving="page.saving" class="mr-2" />
				<back-button />
				<div v-if="$v.$invalid && $v.$anyDirty" class="ml-auto my-0 py-1 alert alert-warning">
					<font-awesome-icon :icon="['fas', 'exclamation-triangle']" fixed-width class="mr-1 text-danger" />
					You have errors in the form.
				</div>
			</fixed-action-bar>
		</b-form>

		<b-modal v-model="page.saveConfirmShow" title="Confirm Save" scrollable size="lg">
			<error-list :errors="page.errors"></error-list>

			<b-form-group label="Add a note to describe changes?" description="Optional">
				<b-form-input type="text" maxLength="100" v-model="page.saveDescription" />
			</b-form-group>

			<div slot="modal-footer">
				<save-button :saving="page.saving" class="mr-2" type="button" @click.native="confirmSave" />
				<b-button type="button" variant="primary" @click="page.saveConfirmShow = false" class="ml-1">Cancel</b-button>
			</div>
		</b-modal>
	</div>
</template>

<script>
	import { required, decimal, integer } from 'vuelidate/lib/validators';
	import _ from 'underscore';

	export default {
		name: 'SrDesignForm',
		props: {
			isUpdate: {
				type: Boolean,
				default: false
			},
			item: {
				type: Object,
				required: true
			},
			project: {
				type: Object,
				required: false
			},
			setOptions: {
				type: Object,
				default: () => {
					return {
						assignedAe: [],
						pointOfContact: [],
						approvingAgent: [],
						finalStatus: [],
					}
				}
			},
			showNoExist: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				apiUrl: 'srdesigns',
				editRoute: 'SrDesignEdit',
				page: {
					errors: [],
					validated: false,
					saving: false,
					saveConfirmShow: false,
					saveDescription: null
				},
				options: {
					projects: [],
					designer: []
				},
				query: {
					project: ''
				}
			}
		},
		computed: {
			queryProject() {
				return this.query.project;
			},
			itemDesigner() {
				return this.item.designer;
			}
		},
		validations: {
			item: {
				projectId: { required, integer },
				assignedAe: {},
				pointOfContact: {},
				status: {},
				notes: {},
				approvingAgent: {},
				finalStatus: {},
				finalDate: {},
				finalContractDate: {}
			},
			query: {
				project: { required }
			}
		},
		watch: {
			queryProject: _.debounce(function (query) { this.findProjects(query) }, 500)
		},
		created() {
			this.get();
		},
		methods: {
			get() {
				if (!this.isNullOrEmpty(this.project)) {
					this.query.project = this.project.name;
					this.item.projectId = this.project.id;
				}

				if (this.isUpdate) {
					this.item.finalDate = this.dateForForm(this.item.finalDate);
					this.item.finalContractDate = this.dateForForm(this.item.finalContractDate);
				}
			},
			async save() {
				this.log(this.item);
				this.page.errors = [];
				this.page.saving = true;

				if (this.$v.$invalid) {
					this.page.errors.push('Please fix the errors below and try again.');
				} else {
					if (this.isUpdate) {
						this.page.saveConfirmShow = true;
					} else {
						try {
							const response = await this.$http.post(this.apiUrl, this.item, this.getTokenHeader());
							this.log(response.data);

							this.$router.push({ name: this.editRoute, params: { id: response.data } });
						} catch (error) {
							if (this.isApiUnauthorized(error)) this.page.showLogin = true;
							else this.page.errors = this.logError(error);
						}
					}
				}

				this.page.saving = false;
			},
			async confirmSave() {
				this.page.errors = [];
				this.page.saving = true;

				if (this.isUpdate) {
					try {
						let data = {
							item1: this.item,
							item2: this.isNullOrEmpty(this.page.saveDescription) ? "Changes saved." : this.page.saveDescription
						};
						const response = await this.$http.put(`${this.apiUrl}/${this.item.projectId}`, data, this.getTokenHeader());
						this.log(response.data);

						this.page.saveConfirmShow = false;
						this.$bvToast.show('changes-saved');
						this.$emit('change');
					} catch (error) {
						if (this.isApiUnauthorized(error)) this.page.showLogin = true;
						else this.page.errors = this.logError(error);
					}
				}

				this.page.saving = false;
			},
			launchToast() {
				this.$bvToast.show('changes-saved');
			},
			logValidations() {
				this.log(this.$v);
			},
			async findProjects(query) {
				try {
					//const response = await this.$http.get(`/srprojects/find-id/${encodeURIComponent(query)}`, this.getTokenHeader());
					const response = await this.$http.post(`/srprojects/find-id-post`, { value: query }, this.getTokenHeader());
					this.options.projects = response.data != '' ? response.data : [];
				} catch (error) {
					this.page.errors = this.logError(error);
				}
			},
			async find(field, query) {
				try {
					const response = await this.$http.get(`/${this.apiUrl}/find/${field}/${query}`, this.getTokenHeader());
					this.options[field] = response.data != '' ? response.data : [];
				} catch (error) {
					this.page.errors = this.logError(error);
				}
			}
		}
	}
</script>
